import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import Sidebar from "../../components/admin/sidebar/Sidebar";
import { ToastContainer } from "react-bootstrap";
import useDash from "../../assets/css/dash.module.css";
import { FontColor, FourGround } from "../../assets/js/theme";
import {
  deleteDeviceFromNetworkDiagram,
  getNetworkDiagram,
} from "../../features/oltApi";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AssignDeviceDialog from "./device/AssignDeviceDialog";
import Footer from "../../components/admin/footer/Footer";
import { useTranslation } from "react-i18next";
import { ArrowLeftShort } from "react-bootstrap-icons";
import DeviceOutputDialog from "./device/DeviceOutputDialog";
import { getNetworkDevice } from "../../features/apiCalls";
import useISPowner from "../../hooks/useISPOwner";

const Diagram = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const svgRef = useRef();
  const { point } = useParams();
  const navigate = useNavigate();

  //---> Get user & current user data form useISPOwner hooks
  const { ispOwnerId } = useISPowner();

  const diagram = useSelector((state) => state.network?.diagram);

  //---> Get netwok device from redux store
  const devices = useSelector((state) => state.network?.devices);

  //---> Get network device outputs form redux store
  const outputs = useSelector((state) => state.network?.outputs);

  //---> Get user network data from redux store
  const networks = useSelector((state) => state.network?.network);

  //---> Get customers form redux store
  const customers = useSelector((state) => state.customer.customer);

  //---> Fiend single network point
  const netwokPoint = networks.find((item) => item._id === point);

  //---> Local State
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [modalStatus, setModalStatus] = useState("");
  const [show, setShow] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [parent, setParent] = useState("");
  const [children, setChildren] = useState();

  useEffect(() => {
    getNetworkDiagram(dispatch, point, setIsLoading);
    getNetworkDevice(dispatch, ispOwnerId, setLoading);
  }, [point]);

  const findAssignableChildrenOfParent = (children = []) => {
    if (!Array.isArray(children)) return [];

    const assignableChild = children.filter(
      (child) => Array.isArray(child.children) && child.children.length > 0
    );

    setChildren(assignableChild);
  };

  const getDeviceOutputsByDiagram = (deviceId, outputId) => {
    const device = devices.find((item) => item.id === deviceId);
    const output = outputs.find((item) => item.id === outputId);

    return {
      device,
      output,
    };
  };

  const getCustomerByCustomerId = (customerId) => {
    const customer = customers.find((item) => item.id === customerId);

    return customer;
  };

  const handleDeleteNetworkDiagramDevice = (data) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this diagram?"
    );

    if (confirmDelete) {
      deleteDeviceFromNetworkDiagram(
        dispatch,
        data.network,
        data._id,
        setLoading
      );
    }
  };

  useEffect(() => {
    svgRef.current.innerHTML = "";

    const marginTop = 30;
    const marginBottom = 30;
    const marginLeft = 100;

    const root = d3.hierarchy(diagram);

    const dx = 50;
    const dy = 300;

    const tree = d3.tree().nodeSize([dx, dy]);
    const diagonal = d3
      .linkHorizontal()
      .x((d) => d.y)
      .y((d) => d.x);

    const width =
      root.height <= 2 ? 450 * root.height + 1 : 350 * root.height + 1;
    const height = dx;

    const svg = d3
      .create("svg")
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "85vh")
      .style("font", `${root.height + 1 * 8}px sans-serif`)
      .style("user-select", "none");

    const gLink = svg
      .append("g")
      .attr("fill", "none")
      .attr("stroke-opacity", 0.6)
      .attr("stroke-width", 1)
      .attr("stroke", "#80918d");

    const gNode = svg
      .append("g")
      .attr("cursor", "pointer")
      .attr("pointer-events", "all");

    function update(event, source, target) {
      const duration = event?.altKey ? 2500 : 500;
      const nodes = root.descendants().reverse();
      const links = root.links();

      tree(root);

      let left = root;
      let right = root;
      root.eachBefore((node) => {
        if (node.x < left.x) left = node;
        if (node.x > right.x) right = node;
      });

      const height = right.x - left.x + marginTop + marginBottom;

      const transition = svg
        .transition()
        .duration(duration)
        .attr("height", height)
        .attr("viewBox", [-marginLeft, left.x - marginTop, width, height])
        .tween(
          "resize",
          window.ResizeObserver ? null : () => () => svg.dispatch("toggle")
        );

      svg
        .selectAll(".link")
        .transition(transition)
        .style("visibility", "visible")
        .attr("opacity", 1);

      // Update the nodes…
      const node = gNode.selectAll("g").data(nodes, (d) => d.id);

      // Enter any new nodes at the parent's previous position.
      const nodeEnter = node
        .enter()
        .append("g")
        .attr("transform", (d) => `translate(${source.y0},${source.x0})`)
        .attr("stroke-linejoin", "round")
        .attr("fill-opacity", 0)
        .attr("stroke-opacity", 0)
        .on("click", (event, d) => {
          if (event.target.tagName.toLowerCase() == "circle") {
            d.children = d?.children ? null : d._children;
            update(event, d);
          }
        });

      nodeEnter
        .append("circle")
        .attr("r", 5)
        .attr("fill", (d) => (d._children ? "green" : "#999"))
        .attr("stroke-width", 10)

        // === Right click -> Device Assign Modal ===
        .on("contextmenu", (event, data) => {
          event.preventDefault();
          console.log({ data });

          if (!data.data.customer) {
            if (!data._children) {
              setModalStatus("assign");
              setParent(data.data);
              setShow(true);
            } else {
              setModalStatus("assign");
              setIsUpdate(true);
              setParent(data);
              setShow(true);
              findAssignableChildrenOfParent(data.children);
            }
          } else {
            toast.error("There is no children. You do not assign device");
          }
        })
        .on("click", (event, d) => {
          event.preventDefault();
          if (d._children) {
            handleDeleteNetworkDiagramDevice(d.data);
          } else {
            toast.error("There is no children. You do not update device");
          }
        });

      // .on("mouseover", handleMouseOverForCircle);

      nodeEnter
        .append("text")
        // .on("click", (e, d) => {
        //   if (d._children) {
        //     updateDeviceDialogHandler(d);
        //   } else {
        //     toast.error("There is no children. You do not update device");
        //   }
        // })
        // .on("contextmenu", (event, d) => {
        //   console.log({ d });

        //   event.preventDefault();
        //   //   if (!d._children) {
        //   //     deviceAssignHandler(d);
        //   //   } else {
        //   //     toast.error(
        //   //       "Already there are children. You cannot assign device."
        //   //     );
        //   //   }
        // })
        .text((d) => {
          const { device, output } = getDeviceOutputsByDiagram(
            d.data.device,
            d.data.output
          );

          const customer = getCustomerByCustomerId(d.data?.customer);

          if ((d.children || d._children) && d.data.parent) {
            return `${output ? output?.portName : ""} (${
              device ? device?.name : ""
            })`.toUpperCase();
          } else {
            return customer
              ? `${customer?.name}`.toUpperCase()
              : `${device ? device?.name : ""} ${
                  output ? output?.portName : ""
                }`.toUpperCase();
          }
        })
        .attr("dy", "0.31em")
        .attr("x", (d) => {
          if (d.data?.customer) return 10;
          if (d.data.parent && d.data.children && d.data.children.length > 0)
            return 10;
          return -10;
        })
        .attr("text-anchor", (d) => {
          if (d.data?.customer) return "start";
          if (d.data.parent && d.data.children && d.data.children.length > 0)
            return "start";
          return "end";
        })

        .clone(true)
        .lower();

      // Transition nodes to their new position.
      node
        .merge(nodeEnter)
        .transition(transition)
        .attr("transform", (d) => `translate(${d.y},${d.x})`)
        .attr("fill-opacity", 1)
        .attr("stroke-opacity", 0.8);

      // Transition exiting nodes to the parent's new position.
      node
        .exit()
        .transition(transition)
        .remove()
        .attr("transform", (d) => `translate(${source.y},${source.x})`)
        .attr("fill-opacity", 0)
        .attr("stroke-opacity", 0);

      // Update the links…
      const link = gLink.selectAll("path").data(links, (d) => d?.target.id);

      //==================== HANDLE HOVER  MOUSE OUT  ==================
      function handleMouseOver() {
        d3.select(this)
          .append("title")
          .attr("class", "tooltip")
          .attr(
            "style",
            `
            background-color: rgba(0, 0, 0, 0.8); /* Background color */
            color: green; /* Text color */
            font-size: 12px; /* Font size */
            padding: 5px; /* Padding */
            border-radius: 5px; /* Border radius */
            /* Add more styles as needed */
        `
          )
          .text((d) => {
            const { device, output } = getDeviceOutputsByDiagram(
              d.target.parent.data.device,
              d.target.data.output
            );

            return `Device Name: ${device?.name || "N/A"} \n Color: ${
              output?.color || "N/A"
            } \n Cable Length: ${
              output?.cableLength || "N/A"
            }m \n Device Brand: ${device?.brand || "N/A"} \n Device Model: ${
              device?.deviceModel || "NA"
            } ${
              device?.ip ? `\n Device IP: ${device.ip}` : ""
            }\n Description : ${output?.description}`;
          });
      }

      // Enter any new links at the parent's previous position.
      const linkEnter = link
        .enter()
        .append("path")
        .attr("id", (d, i) => d.target.data._id)
        .attr("d", (d) => {
          const o = { x: source.x0, y: source.y0 };
          return diagonal({ source: o, target: o });
        })
        .attr("stroke", (d) => {
          const { device, output } = getDeviceOutputsByDiagram(
            d.target.data.device,
            d.target.data.output
          );
          return output?.color || d.target?.data?.wire?.color;
        })
        .style("stroke-width", (d) => 3)
        .style("cursor", "pointer")
        .on("mouseover", handleMouseOver)
        .on("click", (event, d) => {
          setModalStatus("output");
          setParent(d.target.data);
          setShow(true);
        });

      // Transition links to their new position.
      link.merge(linkEnter).transition(transition).attr("d", diagonal);

      // Transition exiting nodes to the parent's new position.
      link
        .exit()
        .transition(transition)
        .remove()
        .attr("d", (d) => {
          const o = { x: source.x, y: source.y };
          return diagonal({ source: o, target: o });
        });

      // Stash the old positions for transition.
      root.eachBefore((d) => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    }

    // Define the zoom behavior
    const zoom = d3.zoom().scaleExtent([0.3, 3]).on("zoom", zoomed);

    svg.call(zoom);

    // Function to handle zoom events
    function zoomed(event) {
      // Update the transformation of the SVG elements
      gNode.attr("transform", event.transform);
      gLink.attr("transform", event.transform);
    }

    root.x0 = dy / 2;
    root.y0 = 0;
    root.descendants().forEach((d, i) => {
      d.id = i;
      d._children = d.children;
    });
    update(null, root);

    // Append the SVG to the React component's DOM node
    svgRef.current.appendChild(svg.node());
  }, [diagram, devices, outputs]);
  // ======================= D3 DIAGRAM IMPLEMENT END  ============

  return (
    <>
      <Sidebar />
      <ToastContainer position="top-right" theme="colored" />
      <div className={useDash.dashboardWrapper}>
        <div className="container-fluied collector">
          <div className="container">
            <FontColor>
              <FourGround>
                <div className="collectorTitle d-flex justify-content-between px-4">
                  <div className="d-flex align-items-center gap-2">
                    <ArrowLeftShort
                      size={30}
                      className="arrowLeftRightButton"
                      onClick={() => navigate(-1)}
                    />

                    <p className="component_name">
                      {" "}
                      {netwokPoint?.name + " " + t("networkDiagram")}
                    </p>
                  </div>
                </div>
              </FourGround>

              <FourGround>
                <div className="overflow-hidden mt-2">
                  <div ref={svgRef}></div>
                </div>
              </FourGround>
              <Footer />
            </FontColor>
          </div>
        </div>
      </div>

      {/* Diagram Assign Device Dialog */}
      {modalStatus === "assign" && (
        <AssignDeviceDialog
          {...{ show, setShow, isUpdate, parent, children }}
        />
      )}

      {/* Diagram Device Output Dialog */}
      {modalStatus === "output" && (
        <DeviceOutputDialog {...{ show, setShow, parent }} />
      )}
    </>
  );
};

export default Diagram;
